/**
 * Referral Rock landing-page detection for the root route (`/`).
 *
 * Mirrors the Partner Landing pattern: when the URL carries `utm_source=referral_rock`
 * the root route renders the RR landing page instead of the default landing. UTMs are
 * propagated through the existing sessionStorage + `navigateToGetStarted` pipeline,
 * so the API callback applies first-touch attribution and captures
 * `pending_referrer_referral_code` exactly as it does for direct invite-link clicks.
 *
 * Shape of an RR redirect (set in Referral Rock → Program → Sharing → Referral Destinations):
 *   https://gozig.com/?utm_source=referral_rock&utm_medium=referral&utm_campaign=member_invite&utm_term={referralCode}&referralCode={referralCode}
 */

/** Normalized value for `utm_source` that counts as Referral Rock invite traffic. */
export const REFERRAL_ROCK_LANDING_UTM_SOURCE = 'referral_rock' as const;

/** Ambassador code is mirrored in `utm_term` and the explicit `referralCode` param. */
export const REFERRAL_ROCK_LANDING_REFERRAL_CODE_PARAM = 'referralCode' as const;
export const REFERRAL_ROCK_LANDING_UTM_TERM_PARAM = 'utm_term' as const;
export const REFERRAL_ROCK_LANDING_UTM_SOURCE_PARAM = 'utm_source' as const;

const MAX_REFERRAL_CODE_LEN = 256;

export interface ReferralRockLandingContext {
    /** True when this URL should render the RR landing page. */
    isReferralRockSource: boolean;
    /** Ambassador `referralCode`, when present. Empty string treated as absent. */
    referralCode?: string;
}

function readSearchParams(search: string): URLSearchParams {
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function normalizeSourceParam(value: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    const normalized = value.trim().toLowerCase().replace(/-/g, '_');
    return normalized.length > 0 ? normalized : undefined;
}

function normalizeReferralCode(value: string | null): string | undefined {
    if (!value) {
        return undefined;
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return undefined;
    }
    return trimmed.length > MAX_REFERRAL_CODE_LEN ? trimmed.slice(0, MAX_REFERRAL_CODE_LEN) : trimmed;
}

export function getReferralRockLandingContext(search: string): ReferralRockLandingContext {
    const params = readSearchParams(search);
    const source = normalizeSourceParam(params.get(REFERRAL_ROCK_LANDING_UTM_SOURCE_PARAM));
    const isReferralRockSource = source === REFERRAL_ROCK_LANDING_UTM_SOURCE;
    const referralCode = normalizeReferralCode(
        params.get(REFERRAL_ROCK_LANDING_REFERRAL_CODE_PARAM)
        ?? params.get(REFERRAL_ROCK_LANDING_UTM_TERM_PARAM),
    );
    return {
        isReferralRockSource,
        ...(referralCode ? { referralCode } : {}),
    };
}

export function shouldShowReferralRockLanding(search: string): boolean {
    return getReferralRockLandingContext(search).isReferralRockSource;
}
